import type { AuthTokens, ApiErrorPayload } from '@/types/api';

let tokenGetter: () => { accessToken: string | null; refreshToken: string | null } = () => ({
  accessToken: null,
  refreshToken: null,
});
let tokenSetter: (tokens: AuthTokens) => void = () => {};
let onUnauthorized: () => void = () => {};

export function configureApiClient(opts: {
  getTokens: () => { accessToken: string | null; refreshToken: string | null };
  setTokens: (tokens: AuthTokens) => void;
  onUnauthorized: () => void;
}): void {
  tokenGetter = opts.getTokens;
  tokenSetter = opts.setTokens;
  onUnauthorized = opts.onUnauthorized;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly payload: ApiErrorPayload | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isMfaRequired(): boolean {
    return this.status === 401 && this.payload?.mfaRequired === true;
  }

  get isMfaEnrollmentRequired(): boolean {
    return this.status === 403 && this.payload?.mfaEnrollmentRequired === true;
  }
}

const API_BASE = (import.meta.env.VITE_API_BASE ?? '') + '/api';

export interface ApiCallOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuthRefresh?: boolean;
}

export async function apiCall<T = unknown>(
  path: string,
  opts: ApiCallOptions = {},
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const { body, skipAuthRefresh, ...rest } = opts;

  const init = buildInit(rest, body);
  let res = await fetch(url, init);

  if (res.status === 401 && !skipAuthRefresh) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await fetch(url, buildInit(rest, body));
    } else {
      onUnauthorized();
    }
  }

  if (!res.ok) {
    const payload = await safeJson(res);
    throw new ApiError(res.status, payload, extractMessage(payload, res));
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function apiUpload<T = unknown>(
  path: string,
  form: FormData,
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const buildHeaders = (): Record<string, string> => {
    const t = tokenGetter();
    const h: Record<string, string> = {};
    if (t.accessToken) h.Authorization = `Bearer ${t.accessToken}`;
    return h;
  };
  let res = await fetch(url, { method: 'POST', body: form, headers: buildHeaders() });
  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await fetch(url, { method: 'POST', body: form, headers: buildHeaders() });
    } else {
      onUnauthorized();
    }
  }
  if (!res.ok) {
    const p = await safeJson(res);
    throw new ApiError(res.status, p, extractMessage(p, res));
  }
  return (await res.json()) as T;
}

function buildInit(rest: Omit<ApiCallOptions, 'body' | 'skipAuthRefresh'>, body: unknown): RequestInit {
  const tokens = tokenGetter();
  const headers = new Headers(rest.headers);
  if (!headers.has('Content-Type') && body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (tokens.accessToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${tokens.accessToken}`);
  }
  return {
    ...rest,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  };
}

// Single-flight refresh: dedupe concurrent 401s so we don't burn the
// rotated refresh token on N parallel calls.
let inflightRefresh: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      const { refreshToken } = tokenGetter();
      if (!refreshToken) return false;
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const tokens = (await res.json()) as AuthTokens;
      tokenSetter(tokens);
      return true;
    } catch {
      return false;
    } finally {
      // Allow next refresh after this one settles.
      setTimeout(() => {
        inflightRefresh = null;
      }, 0);
    }
  })();
  return inflightRefresh;
}

async function safeJson(res: Response): Promise<ApiErrorPayload | null> {
  try {
    return (await res.json()) as ApiErrorPayload;
  } catch {
    return null;
  }
}

/**
 * FU-1 — el mensaje que el backend sí mandó.
 *
 * Esto desenvolvía `message` cuando era string o arreglo, y nada más. Pero el
 * filtro global reenvía TAL CUAL lo que devuelve `exception.getResponse()`, y
 * eso es un objeto en los dos casos que más importan:
 *
 *   ForbiddenException('texto')      -> message: { statusCode, message: 'texto', error }
 *   ConflictException({ code, ... }) -> message: { code, message, ...extras }
 *
 * Así que todo error tipado de la casa —los 409 de doble-booking, los de
 * assignment activo, los 403 de propiedad y de rol— caía al `statusText` y
 * llegaba a pantalla como "Forbidden" o "Conflict". Medido el 09-08 con token de
 * navigator: el 403 traía "Requires role: agent or supervisor or admin" adentro
 * y el usuario veía un error genérico.
 *
 * Se prueban las dos formas, en orden de especificidad, y el `code` queda como
 * último recurso antes del statusText: un código es feo pero es información;
 * "Conflict" no es ninguna.
 */
function extractMessage(payload: ApiErrorPayload | null, res: Response): string {
  const fromMessage = unwrap(payload?.message);
  if (fromMessage) return fromMessage;
  // Algunos errores traen el code arriba en vez de adentro de `message`.
  const topCode = (payload as { code?: unknown } | null)?.code;
  if (typeof topCode === 'string' && topCode.length > 0) return topCode;
  return res.statusText || `Request failed (${res.status})`;
}

function unwrap(value: unknown): string | null {
  if (typeof value === 'string') return value.length > 0 ? value : null;
  if (Array.isArray(value)) {
    const parts = value.filter((v): v is string => typeof v === 'string' && v.length > 0);
    return parts.length > 0 ? parts.join('; ') : null;
  }
  if (value && typeof value === 'object') {
    const o = value as { message?: unknown; code?: unknown };
    // Un nivel de recursión alcanza: Nest anida una sola vez.
    const inner = unwrap(o.message);
    if (inner) return inner;
    if (typeof o.code === 'string' && o.code.length > 0) return o.code;
  }
  return null;
}
